import { languages } from '@/i18n';
import { dashboardI18nAdapter } from '@/i18n/createI18nAdapter';
import { InlineTranslationProvider } from '@/i18n/InlineTranslationProvider';
import posthog from '@/posthogClient';
import * as Sentry from '@sentry/react';
import ReactQueryServerErrorNotifications from '@http/ReactQueryServerErrorNotifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nProvider } from '@eye-able/ui';
import { Provider as JotaiProvider } from 'jotai';
import { useEffect } from 'react';
import { PostHogErrorBoundary, PostHogProvider } from '@posthog/react';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import routes from './routes';

type ResponseError = Error & { status?: number };

const isDev = !import.meta.env.PROD;

let environment = 'development';

if (!isDev) {
  environment =
    window.location.host === 'dashboard.eye-able.com'
      ? 'production'
      : 'staging';
  environment += __SENTRY_ENVIRONMENT_SUFFIX__;
}

// No user data goes to Sentry!
const withoutUser = <T extends { user?: unknown }>(event: T): T => {
  delete event.user;
  return event;
};

Sentry.init({
  dsn: 'https://de07898459224d19a10ceda0d370b34b@o4504741085249536.ingest.sentry.io/4504741227003904',
  integrations: [
    Sentry.reactRouterBrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  // This sets the sample rate to 50% on staging and to 5% on production.
  tracesSampleRate: isDev ? 0 : environment.startsWith('staging') ? 0.5 : 0.05,
  replaysSessionSampleRate: 0.03,
  replaysOnErrorSampleRate: 1.0,
  enabled: import.meta.env.PROD,
  environment,
  beforeSend: withoutUser,
  beforeSendTransaction: withoutUser,
});

const router = Sentry.wrapCreateBrowserRouter(createBrowserRouter)(routes);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (retryCount, error) => {
        const status = (error as ResponseError).status;

        // Check if the error is an instance of an Error and has a status of 401 (unauthorized) or 404 (not found)
        if (status === 401 || status === 404) {
          // Do not retry
          //console.log('Do not retry for 401 or 404 errors');
          return false;
        }
        //retry up to 2 times for all other types of errors
        return retryCount < 2;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchIntervalInBackground: true,
      refetchInterval: 1000 * 60 * 60 * 4, // 4 hours
      staleTime: 1000 * 60 * 60 * 4, // 4 hours
      gcTime: 1000 * 60 * 60 * 4, // 4 hours
    },
  },
});

function App() {
  return (
    <JotaiProvider>
      <QueryClientProvider client={queryClient}>
        <ReactQueryServerErrorNotifications />
        <I18nProvider i18n={dashboardI18nAdapter} languages={languages}>
          <InlineTranslationProvider>
            <PostHogProvider client={posthog}>
              <PostHogErrorBoundary>
                <RouterProvider router={router} />
              </PostHogErrorBoundary>
            </PostHogProvider>
          </InlineTranslationProvider>
        </I18nProvider>
      </QueryClientProvider>
    </JotaiProvider>
  );
}

export default App;
